/** @fileoverview Displays the current tenant's Fabrik billing usage. */

import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import LoadingState from './ui/loading-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { getFabrikUsage } from '../services/billingApi';
import type { BillingUsage } from '../types/billing';
import type { Tenant } from '../types/identity';

type Props = {
  tenant?: Tenant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Loads usage only while the dialog is open, keeping navigation lightweight. */
export default function UsageDialog({ tenant, open, onOpenChange }: Props) {
  const [usage, setUsage] = useState<BillingUsage | null>();
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !tenant) return;

    const controller = new AbortController();
    setUsage(undefined);
    setError('');
    getFabrikUsage(tenant.tenant_id, controller.signal)
      .then(setUsage)
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : 'Unable to load usage.');
        }
      });
    return () => controller.abort();
  }, [open, tenant]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Fabrik usage</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-2">
          {usage === undefined && <LoadingState className="py-4">Loading usage…</LoadingState>}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {usage === null && !error && (
            <Alert>
              <AlertDescription>
                No Fabrik usage plan is assigned to this workspace.
              </AlertDescription>
            </Alert>
          )}
          {usage?.features?.length ? (
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Usage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.features.map((feature) => {
                    const activeCount = feature.usage_details?.active_count ?? 0;
                    const quota = feature.quota;
                    return (
                      <TableRow key={feature.feature_code}>
                        <TableCell className="font-medium">
                          {feature.feature_details?.description || feature.feature_code}
                        </TableCell>
                        <TableCell>
                          <Badge variant={feature.is_active ? 'secondary' : 'outline'}>
                            {feature.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {typeof quota === 'number' ? `${activeCount} / ${quota}` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
